import React, { useContext, useRef } from 'react';
import styles from './styles/NavbarMenu.module.css';
import AuthContext from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { logout } from '../../../services/AuthServices';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faUser, faChalkboardTeacher, faRightFromBracket, faComment, faHome,
    faIdCard, faLayerGroup, faClipboardList, faInbox, faGear, faCirclePlay,
} from '@fortawesome/free-solid-svg-icons';

/**
 * Tabs on the Account page that can be opened straight from the menu. The ids
 * are Account's own `activeTab` values. Shared Scenarios is left out on purpose.
 */
const ACCOUNT_TABS = [
    { tab: 'Profile', label: 'Profile', icon: faIdCard },
    { tab: 'Scenarios', label: 'Scenarios', icon: faLayerGroup },
    { tab: 'Logs', label: 'Logs', icon: faClipboardList },
    { tab: 'Feedback', label: 'Feedback', icon: faInbox },
    { tab: 'Settings', label: 'Settings', icon: faGear },
];

/**
 * @param {Object[]} tutorials  definitions that can be replayed from here, from
 *                              the tutorial registry. Empty when there is nothing
 *                              to offer — on a small screen, or while one is
 *                              already running.
 * @param {Function} onReplayTutorial  called with a tutorial id
 * @param {boolean} replayBlocked  the account cannot create another scenario, so
 *                                 the tour has nothing to build
 * @param {'navbar'|'topbar'} variant  'topbar' is the editor's logo menu: it opens
 *                                     from the left and adds a way back Home,
 *                                     which the editor has no other route to
 */
const NavbarMenu = ({
    setShowMenu,
    user,
    tutorials = [],
    onReplayTutorial,
    replayBlocked = false,
    variant = 'navbar',
}) => {
    const { getLoggedIn } = useContext(AuthContext);
    const navigate = useNavigate();
    const menuRef = useRef(null);

    const isTopbar = variant === 'topbar';

    const handleClickHome = () => {
        navigate('/home');
        setShowMenu(false);
    };

    const handleClickAccountTab = (tab) => {
        if (!user) return;
        navigate(`/user/${user.username}`, { state: { fromNavbar: true, activeTab: tab } });
        setShowMenu(false);
    };

    const handleClickClassrooms = () => {
        navigate('/my-classrooms');
        setShowMenu(false);
    };

    const handleClickLogout = async () => {
        try {
            await logout();
            await getLoggedIn();
            navigate("/logging-out");
            setShowMenu(false);
        } catch (error) {
            console.error('Error during logout:', error);
        }
    };

    const handleClickSiteFeedback = async () => {
        navigate('/site-feedback-page');
        setShowMenu(false);
    }

    const handleClickReplay = (tutorialId) => {
        setShowMenu(false);
        onReplayTutorial?.(tutorialId);
    };

    return (
        <div
            ref={menuRef}
            className={`${styles.dropdown} ${isTopbar ? styles.topbar : ''}`}
            onClick={(e) => e.stopPropagation()}
        >
            {isTopbar && (
                <>
                    <div className={styles.menuItem} onClick={handleClickHome}>
                        <div className={styles.iconContainer}>
                            <FontAwesomeIcon icon={faHome} className={styles.icon} />
                        </div>
                        <div className={styles.textContainer}>
                            <div className={styles.label}>Home</div>
                        </div>
                    </div>
                    <div className={styles.divider} />
                </>
            )}

            {/* A heading for the tabs below it rather than a link of its own. */}
            <div className={styles.menuHeader}>
                <div className={styles.iconContainer}>
                    {user?.profilePicture ? (
                        <img
                            src={user.profilePicture}
                            alt="Profile"
                            className={styles.profilePicture}
                        />
                    ) : (
                        <FontAwesomeIcon icon={faUser} className={styles.icon} />
                    )}
                </div>
                <div className={styles.textContainer}>
                    <div className={styles.headerEyebrow}>Account</div>
                    {user?.username && (
                        <div className={styles.headerName}>{user.username}</div>
                    )}
                </div>
            </div>

            {/* Each tab's left border joins into one rail hanging off the avatar. */}
            <div className={styles.subList}>
                {ACCOUNT_TABS.map(({ tab, label, icon }) => (
                    <div
                        key={tab}
                        className={styles.subItem}
                        onClick={() => handleClickAccountTab(tab)}
                    >
                        <FontAwesomeIcon icon={icon} className={styles.subIcon} />
                        <div className={styles.subLabel}>{label}</div>
                    </div>
                ))}

                {tutorials.map((tutorial) => (
                    <div
                        key={tutorial.id}
                        className={`${styles.subItem} ${replayBlocked ? styles.disabled : ''}`}
                        onClick={() => !replayBlocked && handleClickReplay(tutorial.id)}
                        aria-disabled={replayBlocked}
                    >
                        <FontAwesomeIcon icon={faCirclePlay} className={styles.subIcon} />
                        <div className={styles.textContainer}>
                            <div className={styles.subLabel}>{tutorial.replay.label}</div>
                            <div className={styles.subtitle}>
                                {replayBlocked ? tutorial.replay.unavailable : tutorial.replay.note}
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className={styles.divider} />

            <div className={styles.menuItem} onClick={handleClickClassrooms}>
                <div className={styles.iconContainer}>
                    <FontAwesomeIcon icon={faChalkboardTeacher} className={styles.icon} />
                </div>
                <div className={styles.textContainer}>
                    <div className={styles.label}>Classrooms</div>
                </div>
            </div>

            <div className={styles.menuItem} onClick={handleClickSiteFeedback}>
                <div className={styles.iconContainer}>
                    <FontAwesomeIcon icon={faComment} className={styles.icon} />
                </div>
                <div className={styles.textContainer}>
                    <div className={styles.label}>Give Feedback</div>
                </div>
            </div>

            <div className={styles.divider} />

            <div className={styles.menuItem} onClick={handleClickLogout}>
                <div className={styles.iconContainer}>
                    <FontAwesomeIcon icon={faRightFromBracket} className={styles.icon} />
                </div>
                <div className={styles.textContainer}>
                    <div className={styles.label}>Logout</div>
                </div>
            </div>
        </div>
    );
};

export default NavbarMenu;
